import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { goToCommunity } from '../utils/navigation';
import { Users, Calendar, Heart, LogOut, Home, MapPin, Clock, Plus, Search } from 'lucide-react';
import { useRoleBasedEvents } from '../hooks/useRoleBasedEvents';
import EventCard from './EventCard';
import EventDetailModal from './EventDetailModal';
import Pagination from './Pagination';
import EventFormWithMap from './EventFormWithMap';
import { apiService } from '../services/api';
import { DASHBOARD_CONFIG } from '../config/dashboard';

const CommunityDashboard: React.FC = () => {
  const { user, logout, token } = useAuth();
  const [activeTab, setActiveTab] = useState<'overview' | 'events'>('overview');
  // Always use my events since we removed the source filter buttons
  const eventSource = 'my';
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [showEventModal, setShowEventModal] = useState(false);
  // We're not using gallery functionality currently but keeping for future use
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [galleryIndex, setGalleryIndex] = useState(0);
  
  // Modal state for event creation
  const [showCreateEventModal, setShowCreateEventModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [eventFormLoading, setEventFormLoading] = useState(false);
  const [userStats, setUserStats] = useState({
    eventsAttended: DASHBOARD_CONFIG.DEFAULT_STATS.EVENTS_ATTENDED,
    eventsCreated: DASHBOARD_CONFIG.DEFAULT_STATS.EVENTS_CREATED,
    communityPoints: DASHBOARD_CONFIG.DEFAULT_STATS.COMMUNITY_POINTS,
    upcomingEvents: DASHBOARD_CONFIG.DEFAULT_STATS.UPCOMING_EVENTS
  });
  
  // Search and pagination state
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DASHBOARD_CONFIG.DEFAULT_PAGE_SIZE);

  // Fetch events based on user role and selected source
  const { 
    events: normalizedEvents, 
    loading: eventsLoading, 
    refetch: refetchEvents,
    pagination
  } = useRoleBasedEvents(eventSource, {
    page: currentPage,
    limit: pageSize,
    search: searchTerm || undefined,
    sortBy: 'CREATED_AT',
    sortOrder: 'DESC'
  });

  const [upcomingEvents, setUpcomingEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        // Fetch real stats from API
        if (token) {
          try {
            const statsResponse = await apiService.getEventStats(token);
            if (statsResponse.data) {
              setUserStats({
                eventsAttended: statsResponse.data.eventsAttended || 0,
                eventsCreated: statsResponse.data.eventsCreated || 0,
                communityPoints: statsResponse.data.communityPoints || 0,
                upcomingEvents: statsResponse.data.upcomingEvents || 0
              });
            }
          } catch (error) {
            console.error('Error fetching user stats:', error);
            // Keep default values if API fails
          }
          
          try {
            const upcomingResponse = await apiService.getUpcomingEvents(DASHBOARD_CONFIG.UPCOMING_EVENTS_LIMIT);
            if (upcomingResponse.data) {
              setUpcomingEvents(upcomingResponse.data);
            } else {
              setUpcomingEvents([]);
            }
          } catch (error) {
            console.error('Error fetching upcoming events:', error);
            setUpcomingEvents([]);
          }
        }
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, [token]);

  const handleLogout = () => {
    logout();
  };
  
  // Helper functions for event display formatting - currently used in the normalized events
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const formatDateRange = (startDate?: string, endDate?: string) => {
    if (!startDate) return 'Date TBA';
    
    try {
      const start = new Date(startDate);
      const end = endDate ? new Date(endDate) : start;
      
      const startMonth = start.toLocaleDateString('en-US', { month: 'short' });
      const endMonth = end.toLocaleDateString('en-US', { month: 'short' });
      const startDay = start.getDate();
      const endDay = end.getDate();
      const year = start.getFullYear();
      
      if (startMonth === endMonth) {
        return `${startMonth} ${startDay}-${endDay}, ${year}`;
      } else {
        return `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${year}`;
      }
    } catch {
      return 'Date TBA';
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const formatAttendees = (count?: number) => {
    if (!count) return 'Open to all';
    if (count >= DASHBOARD_CONFIG.ATTENDEE_COUNT_THRESHOLD) {
      return `${Math.floor(count / DASHBOARD_CONFIG.ATTENDEE_COUNT_THRESHOLD)}${DASHBOARD_CONFIG.ATTENDEE_COUNT_SUFFIX}`;
    }
    return `${count}+`;
  };
  
  // Handle view event details
  const handleViewEventDetails = (event: any) => {
    setSelectedEvent(event);
    setShowEventModal(true);
    setGalleryIndex(0);
  };
  
  // Handle search input change
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page on new search
  }, []);
  
  // Handle page change
  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleNavigation = (path: string) => {
    if (path === '/community') {
      goToCommunity();
    }
  };
  
  // Handle event creation
  const handleCreateEvent = async (eventData: FormData) => {
    if (!token) return;
    
    setEventFormLoading(true);
    try {
      await apiService.createEvent(eventData, token);
      setShowCreateEventModal(false);
      refetchEvents();
    } catch (error) {
      console.error('Error creating event:', error);
      throw error;
    } finally {
      setEventFormLoading(false);
    }
  };

  // Handle event editing
  const handleEditEvent = (event: any) => {
    setSelectedEvent(event);
    setShowEventModal(false);
    setShowEditModal(true);
  };

  // Handle event update
  const handleUpdateEvent = async (eventData: any) => {
    if (!selectedEvent || !token) return;
    
    setEventFormLoading(true);
    try {
      await apiService.updateEvent(selectedEvent.id || selectedEvent.EVENT_ID, eventData, token);
      setShowEditModal(false);
      setSelectedEvent(null);
      refetchEvents();
    } catch (error) {
      console.error('Error updating event:', error);
      throw error;
    } finally {
      setEventFormLoading(false);
    }
  };

  if (!user || !['COMMITTEE'].includes(user.role)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
          <p className="text-gray-600">You don't have permission to access this dashboard.</p>
        </div>
      </div>
    );
  }

  const renderOverviewTab = () => (
    <>
        {/* User Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Calendar className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Events Attended</p>
                <p className="text-2xl font-bold text-gray-900">{userStats.eventsAttended}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <Heart className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Events Created</p>
                <p className="text-2xl font-bold text-gray-900">{userStats.eventsCreated}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Users className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Committee Points</p>
                <p className="text-2xl font-bold text-gray-900">{userStats.communityPoints}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Clock className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Upcoming Events</p>
                <p className="text-2xl font-bold text-gray-900">{userStats.upcomingEvents}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <button
          onClick={() => setActiveTab('events')}
            className="bg-white rounded-lg shadow p-6 hover:shadow-md transition-shadow duration-200 text-left group"
          >
            <div className="flex items-center justify-between">
              <div>
                <Calendar className="h-8 w-8 text-dpn-pink mb-3" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Browse Events</h3>
                <p className="text-gray-600">Discover and join community events</p>
              </div>
              <div className="text-dpn-pink group-hover:translate-x-1 transition-transform duration-200">
                →
              </div>
            </div>
          </button>

          <button
          onClick={() => setActiveTab('events')}
            className="bg-white rounded-lg shadow p-6 hover:shadow-md transition-shadow duration-200 text-left group"
          >
            <div className="flex items-center justify-between">
              <div>
                <Heart className="h-8 w-8 text-dpn-pink mb-3" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Create Event</h3>
                <p className="text-gray-600">Organize and share your events</p>
              </div>
              <div className="text-dpn-pink group-hover:translate-x-1 transition-transform duration-200">
                →
              </div>
            </div>
          </button>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <MapPin className="h-8 w-8 text-gray-400 mb-3" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Committee Map</h3>
                <p className="text-gray-600">Explore events near you</p>
              </div>
              <div className="text-gray-400">
                →
              </div>
            </div>
          </div>
        </div>

        {/* Upcoming Events */}
        <div className="bg-white rounded-lg shadow mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Upcoming Events</h3>
          </div>
          <div className="p-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-dpn-pink mx-auto mb-4"></div>
                             <p className="text-gray-500">{DASHBOARD_CONFIG.MESSAGES.LOADING.UPCOMING_EVENTS}</p>
                  </div>
          ) : upcomingEvents.length === 0 ? (
            <div className="text-center py-8">
              <Calendar className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                             <h3 className="text-lg font-medium text-gray-900 mb-2">{DASHBOARD_CONFIG.MESSAGES.NO_DATA.UPCOMING_EVENTS}</h3>
               <p className="text-gray-500">{DASHBOARD_CONFIG.MESSAGES.EMPTY_STATE.UPCOMING_EVENTS}</p>
                </div>
          ) : (
            <div className="space-y-4">
              {upcomingEvents.map((event: any, index: number) => (
                <div key={index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                    <h4 className="font-medium text-gray-900">{event.title || 'Untitled Event'}</h4>
                    <p className="text-sm text-gray-600">{event.description || 'No description available'}</p>
                  <div className="flex items-center space-x-4 mt-2">
                    <span className="flex items-center text-xs text-gray-500">
                      <MapPin className="h-3 w-3 mr-1" />
                        {event.location || 'Location not set'}
                    </span>
                    <span className="flex items-center text-xs text-gray-500">
                      <Clock className="h-3 w-3 mr-1" />
                        {event.date || 'Date not set'}
                    </span>
                  </div>
                </div>
                <button className="px-4 py-2 bg-dpn-pink text-white rounded-md hover:bg-dpn-raspberry transition-colors duration-200">
                  Join
                </button>
              </div>
              ))}
            </div>
          )}
          </div>
        </div>

        {/* Committee Activity */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Recent Committee Activity</h3>
          </div>
          <div className="p-6">
          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-dpn-pink mx-auto mb-4"></div>
                             <p className="text-gray-500">{DASHBOARD_CONFIG.MESSAGES.LOADING.COMMUNITY_ACTIVITY}</p>
            </div>
          ) : (
            <div className="text-center py-8">
                             <p className="text-gray-500">{DASHBOARD_CONFIG.MESSAGES.NO_DATA.COMMUNITY_ACTIVITY}</p>
            </div>
          )}
        </div>
      </div>
    </>
  );

  const renderEventsTab = () => (
    <>
      {/* Events Header */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Committee Events</h2>
            <p className="text-gray-600">Discover and join events in your community</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <button 
              onClick={() => setShowCreateEventModal(true)}
              className="flex items-center space-x-2 bg-dpn-pink hover:bg-dpn-raspberry text-white px-4 py-2 rounded-lg transition-colors duration-200"
            >
              <Plus className="h-4 w-4" />
              <span>Create Event</span>
            </button>
          </div>
        </div>
      </div>

             {/* Search Bar */}
       <div className="bg-white rounded-lg shadow p-6 mb-6">
         <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
           <div className="flex-1">
             <div className="relative">
               <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
               <input
                 type="text"
                 placeholder="Search events..."
                 value={searchTerm}
                 onChange={handleSearchChange}
                 className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dpn-pink focus:border-transparent"
               />
             </div>
           </div>
         </div>
       </div>

      {/* Events List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Available Events ({normalizedEvents.length})</h3>
        </div>
        <div className="p-6">
          {eventsLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-dpn-pink mx-auto mb-4"></div>
                             <p className="text-gray-500">{DASHBOARD_CONFIG.MESSAGES.LOADING.EVENTS}</p>
            </div>
          ) : normalizedEvents.length === 0 ? (
            <div className="text-center py-8">
              <Calendar className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                             <h3 className="text-lg font-medium text-gray-900 mb-2">{DASHBOARD_CONFIG.MESSAGES.NO_DATA.EVENTS}</h3>
               <p className="text-gray-500">{DASHBOARD_CONFIG.MESSAGES.NO_DATA.CREATE_FIRST_EVENT}</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {normalizedEvents.map((event: any) => (
                  <EventCard 
                    key={event.id} 
                    event={event} 
                    onViewDetails={handleViewEventDetails}
                    onEdit={handleEditEvent}
                    canEdit={true}
                    user={user}
                  />
                ))}
              </div>
              
              {/* Pagination */}
              {pagination && (
                <Pagination 
                  currentPage={pagination.currentPage || currentPage}
                  totalPages={pagination.totalPages || 1}
                  onPageChange={handlePageChange}
                  className="mt-6"
                />
              )}
            </>
          )}
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Event Details Modal */}
      <EventDetailModal
        event={selectedEvent}
        isOpen={showEventModal}
        onClose={() => setShowEventModal(false)}
        onEdit={handleEditEvent}
        canEdit={true}
      />
      
      {/* Create Event Modal */}
      {showCreateEventModal && (
        <EventFormWithMap
          event={null}
          onClose={() => setShowCreateEventModal(false)}
          onSubmit={handleCreateEvent}
          loading={eventFormLoading}
          title="Create New Event"
        />
      )}

      {/* Edit Event Modal */}
      {showEditModal && selectedEvent && (
        <EventFormWithMap
          event={selectedEvent}
          onClose={() => {
            setShowEditModal(false);
            setSelectedEvent(null);
          }}
          onSubmit={handleUpdateEvent}
          loading={eventFormLoading}
          title="Edit Event"
        />
       )}
      
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <Users className="h-8 w-8 text-dpn-pink" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Committee Dashboard</h1>
                <p className="text-sm text-gray-600">Welcome to your community space, {user.role}</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => handleNavigation('/community')}
                className="flex items-center space-x-2 text-dpn-olive hover:text-dpn-pink transition-colors duration-200"
              >
                <Home className="h-5 w-5" />
                <span>Home</span>
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 text-dpn-olive hover:text-dpn-pink transition-colors duration-200"
              >
                <LogOut className="h-5 w-5" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tab Navigation */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-6 py-4 text-sm font-medium transition-colors duration-200 ${
                activeTab === 'overview'
                  ? 'text-dpn-pink border-b-2 border-dpn-pink'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('events')}
              className={`px-6 py-4 text-sm font-medium transition-colors duration-200 ${
                activeTab === 'events'
                  ? 'text-dpn-pink border-b-2 border-dpn-pink'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Events
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && renderOverviewTab()}
        {activeTab === 'events' && renderEventsTab()}
      </div>
    </div>
  );
};

export default CommunityDashboard;
